

























































































import { computed, defineComponent, ref, watch } from "@vue/composition-api";
import { InputJoinData } from "@scrile/api-provider/dist/api/LoginProvider";
import useCheckUsername from "@/hooks/useCheckUsername";
import { handleRequestError } from "@/lib/handleRequestError";
import { $t } from "@/lib/i18nHelpers";
import { loginSignUp, olecamsSignUp } from "@/lib/loginHelpers";
import config from "@/config";
import { FormFields, FormValues } from "@/types";
import { ROLES } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseCheckbox from "@/components/BaseCheckbox";
import BaseForm from "@/components/BaseForm";
import BaseIcon from "@/components/BaseIcon";
import BaseInput from "@/components/BaseInput";
import BaseSpinner from "@/components/BaseSpinner";
import LoginContainer from "@/components/LoginContainer";

export default defineComponent({
  name: "SignUpComponent",
  components: { BaseSpinner, BaseInput, BaseIcon, BaseCheckbox, BaseForm, BaseButton, LoginContainer },
  props: {
    role: { type: String, default: config.userRegistrationRole },
    title: { type: String },
    forceDark: { type: Boolean, default: false },
    hideLogo: { type: Boolean, default: false },
    hideLinks: { type: Boolean, default: false },
    hideSubmitButton: { type: Boolean, default: false },
    hideTitle: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const loading = ref(false);
    const isValid = ref(false);
    const errorFields = ref<string[]>([]);

    const isCreatorRegistrationRole = computed(() => props.role === ROLES.CREATOR);
    const { processingUsername, isValidScreenName, checkUsername } = useCheckUsername(!isCreatorRegistrationRole.value);

    const values = ref<FormValues>({});
    const validate = ref<() => Promise<void>>(async () => {});

    const fields = computed<FormFields>(() => [
      {
        name: "email",
        placeholder: $t("Your email"),
        autocomplete: "email",
        type: "email",
        rules: "required",
        forceDark: props.forceDark,
        invalidField: errorFields.value.includes("email"),
        hint: errorFields.value.includes("email") ? $t("This email is already in use.") : undefined,
      },
      {
        name: "emailHint",
        type: "emailHint",
      },
      {
        name: "screenName",
        placeholder: $t("Choose a username"),
        type: "text",
        rules: isCreatorRegistrationRole.value ? "required" : [],
        forceDark: props.forceDark,
        invalidField: !isValidScreenName.value,
        rulesMessages: { required: $t("Please fill out this field") },
      },
      {
        name: "password",
        type: "password",
        placeholder: $t("Enter a password"),
        autocomplete: "new-password",
        rules: isCreatorRegistrationRole.value ? ["required", "password_strong"] : ["required", "min:4"],
        forceDark: props.forceDark,
      },

      {
        name: "ageConfirm",
        label: $t("By signing up, you confirm that you are at least 18 years of age."),
        type: "checkbox",
        rules: "required",
        forceDark: props.forceDark,
      },

      {
        name: "terms",
        type: "checkbox",
        rules: "required",
      },
    ]);

    const usernameAppendIcon = computed(() =>
      !processingUsername.value && values.value.screenName && isValidScreenName.value ? "sci-check-line" : undefined
    );
    const usernameHint = computed(() =>
      !processingUsername.value && values.value.screenName && !isValidScreenName.value
        ? $t("This username is already in use.")
        : undefined
    );
    const isValidForm = computed(
      () =>
        isValid.value &&
        values.value.ageConfirm &&
        values.value.terms &&
        isValidScreenName.value &&
        !processingUsername.value
    );

    const onInput = () => {
      errorFields.value = [];
    };

    const onScreenNameInput = (value: string) => {
      values.value = { ...values.value, screenName: value };
      checkUsername(value);
    };

    const onTermsChange = (value: boolean) => {
      values.value = { ...values.value, terms: value };
    };

    const submit = async () => {
      await validate.value();
      if (!isValidForm.value) return;
      try {
        loading.value = true;
        const { email, password, screenName } = values.value;
        const data: InputJoinData = {
          email,
          password,
          screenName,
          role: props.role,
        };
        if (isCreatorRegistrationRole.value) {
          await loginSignUp(data);
        } else {
          await olecamsSignUp(data);
        }
        emit("signup");
      } catch (e) {
        handleRequestError(e, [
          {
            comparator: e => e.details?.messageTemplate === "Invalid email",
            onSuccessCB: () => {
              errorFields.value.push("email");
            },
          },
        ]);
      } finally {
        loading.value = false;
      }
    };

    emit("setCb", submit);

    watch(isValidForm, v => emit("setValid", v), { immediate: true });

    return {
      loading,
      isValid,
      fields,
      values,
      processingUsername,
      usernameAppendIcon,
      usernameHint,
      validate,
      onScreenNameInput,
      onTermsChange,
      submit,
      onInput,
    };
  },
});
